import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Settings,
  Building2,
  Key,
  Printer,
  Check,
  Loader2,
  AlertCircle,
  RefreshCw,
  Save,
  MapPin,
  ShoppingBag,
  Link,
  Unlink,
  Store,
  DollarSign,
  HelpCircle,
  Mail,
  X
} from "lucide-react";
import { toast } from "sonner";
import PlaceSearchDropdown from "../components/PlaceSearchInput";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";

const DEVELOPER_EMAIL = "starflightzero@gmail.com";

const SettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpMessage, setHelpMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  
  // Shopify connection state
  const [shopifyStatus, setShopifyStatus] = useState(null);
  const [shopifyLoading, setShopifyLoading] = useState(false);
  const [shopifyConnecting, setShopifyConnecting] = useState(false);
  const [shopifyForm, setShopifyForm] = useState({
    shop_domain: ""
  });
  const [showReviewerPopup, setShowReviewerPopup] = useState(
    () => localStorage.getItem("ew_reviewer_popup_dismissed") !== "1"
  );
  const [billing, setBilling] = useState(null);
  const [billingBusy, setBillingBusy] = useState(false);

  const fetchBilling = async () => {
    try {
      const res = await axios.get(`${API}/shopify/billing/status`);
      setBilling(res.data);
    } catch (e) { /* ignore transient */ }
  };

  const startSubscribe = async () => {
    setBillingBusy(true);
    try {
      const res = await axios.post(`${API}/shopify/billing/subscribe`, {});
      if (res.data?.confirmationUrl) {
        window.location.assign(res.data.confirmationUrl);
      } else {
        toast.error("Could not start checkout. Please try again.");
        setBillingBusy(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout.");
      setBillingBusy(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchShopifyStatus();
    fetchBilling();
    
    // Check for OAuth redirect results
    const urlParams = new URLSearchParams(window.location.search);
    const shopifyConnected = urlParams.get('shopify_connected');
    const shopifyError = urlParams.get('shopify_error');
    const billingReturn = urlParams.get('billing');
    
    if (shopifyConnected === 'true') {
      toast.success("Successfully connected to Shopify!");
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (shopifyError) {
      toast.error(`Shopify connection failed: ${shopifyError}`);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (billingReturn === 'return') {
      toast.success("Subscription updated — thank you!");
      window.history.replaceState({}, '', window.location.pathname);
      fetchBilling();
    }
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const fetchShopifyStatus = async () => {
    setShopifyLoading(true);
    try {
      const response = await axios.get(`${API}/shopify/connection-status`);
      setShopifyStatus(response.data);
    } catch (error) {
      console.error("Error fetching Shopify status:", error);
      setShopifyStatus({ connected: false, message: "Couldn't check connection — please try again" });
    } finally {
      setShopifyLoading(false);
    }
  };

  const connectShopify = async () => {
    if (!shopifyForm.shop_domain) {
      toast.error("Please enter your shop domain");
      return;
    }
    
    setShopifyConnecting(true);
    
    // Clean up shop domain - handle various input formats
    let shopDomain = shopifyForm.shop_domain.trim().toLowerCase();
    shopDomain = shopDomain.replace(/^https?:\/\//, '');
    shopDomain = shopDomain.replace(/\/+$/, '');
    shopDomain = shopDomain.split('/')[0];
    if (shopDomain.endsWith('.myshopify.com')) {
      // Already correct format
    } else if (shopDomain.includes('.myshopify')) {
      shopDomain = `${shopDomain}.com`;
    } else {
      shopDomain = `${shopDomain}.myshopify.com`;
    }
    
    // Start an authenticated OAuth reconnect. begin-connect records THIS org in the
    // OAuth state so the callback links the store to us (fixes orphaned reconnects).
    // The backend routes every store through the PUBLIC app (the universal connector);
    // a specific store can be pinned to the custom app via SHOPIFY_CUSTOM_SHOP if ever needed.
    try {
      const resp = await axios.post(`${API}/shopify/begin-connect?shop=${encodeURIComponent(shopDomain)}`);
      if (resp.data?.authorize_url) {
        window.location.href = resp.data.authorize_url;
        return;
      }
      throw new Error("No authorize URL returned");
    } catch (err) {
      console.error("begin-connect failed:", err);
      toast.error("Could not start Shopify connection. Please try again.");
      setShopifyConnecting(false);
    }
  };

  const disconnectShopify = async () => {
    if (!confirm("Are you sure you want to disconnect from Shopify?")) return;
    
    try {
      await axios.post(`${API}/shopify/disconnect`);
      toast.success("Disconnected from Shopify");
      fetchShopifyStatus();
    } catch (error) {
      toast.error("Failed to disconnect");
    }
  };

  const updateSettings = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  // Auto-save specific settings immediately
  const updateAndSaveSettings = async (field, value) => {
    const newSettings = { ...settings, [field]: value };
    setSettings(newSettings);
    try {
      await axios.put(`${API}/settings`, { [field]: value });
      toast.success(`${field === 'order_sync_days' ? 'Order sync range' : field} updated`);
    } catch (error) {
      toast.error("Failed to save setting");
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings);
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const synchronise = async () => {
    setTesting(true);
    setConnectionStatus(null);
    try {
      // Save first to ensure credentials are updated
      await axios.put(`${API}/settings`, settings);
      
      // Test connection
      const testResponse = await axios.post(`${API}/settings/test-connection`);
      
      if (testResponse.data.success) {
        // Connection successful - now sync waybills and PODs
        toast.success("Connection successful! Syncing data...");
        
        try {
          const syncResponse = await axios.post(`${API}/ff/sync-history?days=30`);
          const syncData = syncResponse.data;
          
          setConnectionStatus({
            success: true,
            message: `Synced ${syncData.waybills_synced} waybills, ${syncData.pods_synced} PODs (${syncData.filtered_collections} of ${syncData.total_collections} total)`
          });
          toast.success(`Synced ${syncData.waybills_synced} waybills, ${syncData.pods_synced} PODs`);
        } catch (syncError) {
          // Connection worked but sync may have issues
          const errorMsg = syncError.response?.data?.detail || "Sync partially completed";
          setConnectionStatus({
            success: true,
            message: `Connected. ${errorMsg}`
          });
          toast.warning(errorMsg);
        }
      } else {
        setConnectionStatus(testResponse.data);
        toast.error(testResponse.data.message || "Connection failed");
      }
    } catch (error) {
      setConnectionStatus({ success: false, message: "Connection test failed" });
      toast.error("Connection test failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#16a34a]" />
      </div>
    );
  }

  const sendHelpEmail = () => {
    const userEmail = localStorage.getItem("ff_user") || "";
    const shop = shopifyStatus?.shop || settings?.shopify_shop || "";
    const subject = encodeURIComponent("EasyWaybill – Developer Assistance Request");
    const body = encodeURIComponent(
      [
        "Hi EasyWaybill team,",
        "",
        "I'd like some developer assistance:",
        helpMessage || "(please describe your issue)",
        "",
        "----------------------------------------",
        `Account: ${userEmail || "N/A"}`,
        `Store: ${shop || "N/A"}`,
        "Sent from EasyWaybill → Settings",
      ].join("\n")
    );
    window.location.href = `mailto:${DEVELOPER_EMAIL}?subject=${subject}&body=${body}`;
    toast.success("Opening your email app to contact the developer…");
    setHelpOpen(false);
    setHelpMessage("");
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            Settings
          </h1>
          <p className="text-slate-500 mt-1">Configure your courier integration</p>
        </div>
        <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setHelpOpen(true)}
          data-testid="help-developer-btn"
          title="Need developer help?"
          aria-label="Get developer assistance"
          className="w-9 h-9 rounded-full border border-slate-300 text-slate-500 hover:text-[#16a34a] hover:border-[#16a34a] hover:bg-green-50 flex items-center justify-center transition-colors"
        >
          <HelpCircle className="w-5 h-5" />
        </button>
        <Button 
          onClick={saveSettings}
          disabled={saving}
          className="bg-[#16a34a] hover:bg-[#15803d] text-white"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
        </div>
      </div>

      {/* Developer assistance dialog */}
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="sm:max-w-md" data-testid="help-developer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-[#16a34a]" />
              Developer Assistance
            </DialogTitle>
            <DialogDescription>
              Need a hand? Send a message to our developer and we'll get back to you by email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="help-message">How can we help?</Label>
            <Textarea
              id="help-message"
              value={helpMessage}
              onChange={(e) => setHelpMessage(e.target.value)}
              placeholder="Describe the issue or question you need help with…"
              rows={4}
              data-testid="help-message-input"
            />
            <p className="text-xs text-slate-400">
              We'll email <span className="font-medium text-slate-500">{DEVELOPER_EMAIL}</span> with your account details attached.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setHelpOpen(false)}
              data-testid="help-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={sendHelpEmail}
              className="bg-[#16a34a] hover:bg-[#15803d] text-white"
              data-testid="help-send-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Email Developer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Shopify Connection */}
        <Card className="border-slate-200 shadow-sm lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShoppingBag className="w-5 h-5 text-[#96BF48]" />
              Shopify Connection
            </CardTitle>
            <CardDescription>
              Connect your Shopify store to import orders
            </CardDescription>
          </CardHeader>
          <CardContent>
            {shopifyLoading ? (
              <div className="flex items-center gap-2 text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Checking connection...
              </div>
            ) : shopifyStatus?.connected ? (
              <div className="space-y-4">
                {/* Connected Status */}
                <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Store className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-green-800">Connected</span>
                        <Check className="w-4 h-4 text-green-600" />
                      </div>
                      <p className="text-sm text-green-700">
                        {shopifyStatus.shop_name || shopifyStatus.shop}
                      </p>
                      {shopifyStatus.shop_email && (
                        <p className="text-xs text-green-600">{shopifyStatus.shop_email}</p>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={disconnectShopify}
                    className="border-red-200 text-red-600 hover:bg-red-50"
                    data-testid="disconnect-shopify-btn"
                  >
                    <Unlink className="w-4 h-4 mr-1" />
                    Disconnect
                  </Button>
                </div>
                
                {/* Connection Details */}
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Shop Domain</p>
                    <p className="font-mono text-slate-700">{shopifyStatus.shop}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Currency</p>
                    <p className="font-medium text-slate-700">{shopifyStatus.currency || "ZAR"}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Last Sync</p>
                    <p className="text-slate-700">
                      {shopifyStatus.last_sync 
                        ? new Date(shopifyStatus.last_sync).toLocaleString()
                        : "Never"
                      }
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Not Connected Warning */}
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  <span className="text-amber-800">
                    {shopifyStatus?.message || "Shopify not connected"}
                  </span>
                </div>
                
                {/* Connection Form - OAuth Flow */}
                <div className="max-w-md">
                  <Label className="text-slate-700">Shop Domain</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={shopifyForm.shop_domain}
                      onChange={(e) => setShopifyForm({ ...shopifyForm, shop_domain: e.target.value })}
                      className="border-slate-200"
                      placeholder="your-store.myshopify.com"
                      data-testid="shopify-domain"
                      onKeyDown={(e) => e.key === 'Enter' && connectShopify()}
                    />
                    <Button
                      onClick={connectShopify}
                      disabled={shopifyConnecting || !shopifyForm.shop_domain}
                      className="bg-[#96BF48] hover:bg-[#7AA83D] text-white whitespace-nowrap"
                      data-testid="connect-shopify-btn"
                    >
                      {shopifyConnecting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        <>
                          <Link className="w-4 h-4 mr-2" />
                          Connect
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Enter your store domain and click Connect, then approve EasyWaybill on Shopify.
                  </p>

                  <p className="text-xs text-slate-500 mt-1">
                    You must be the store's owner to approve access. Once connected, your store
                    reconnects automatically each time you log in.
                  </p>

                  {/* Dismissible reviewer pop-up */}
                  {showReviewerPopup && (
                    <div
                      className="mt-3 relative rounded-lg bg-amber-50 border border-amber-300 shadow-md p-3 pr-8"
                      data-testid="shopify-reviewer-note"
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setShowReviewerPopup(false);
                          try { localStorage.setItem("ew_reviewer_popup_dismissed", "1"); } catch (e) {}
                        }}
                        className="absolute top-2 right-2 text-amber-500 hover:text-amber-700 transition-colors"
                        aria-label="Close"
                        data-testid="shopify-reviewer-note-close"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <div className="flex items-start gap-2">
                        <HelpCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-amber-900 leading-relaxed">
                          <p className="font-semibold mb-0.5">For Shopify App Store reviewers</p>
                          <p>
                            Please install EasyWaybill from the <strong>App Store listing</strong> or
                            the install link in our submission — not by entering a domain here. This is
                            Shopify's expected flow for apps still in review.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Subscription / Billing */}
        <Card className="border-slate-200 shadow-sm self-start" data-testid="subscription-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <DollarSign className="w-5 h-5 text-[#16a34a]" />
              Subscription
            </CardTitle>
            <CardDescription>
              Manage your EasyWaybill plan — billed securely through Shopify
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900" data-testid="subscription-status">
                  {billing?.status === "active" ? "Active subscription" :
                   billing?.trial ? "Free trial" :
                   billing?.status === "not_connected" ? "Connect Shopify to subscribe" :
                   billing?.status === "billing_skipped" ? "Billing disabled (dev)" :
                   "No active subscription"}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {billing?.plan_name || "EasyWaybill"} — ${billing?.price ?? 29}/month {billing?.currency || "USD"}
                  {billing?.trial_ends ? ` · trial ends ${new Date(billing.trial_ends).toLocaleDateString()}` : ""}
                </div>
              </div>
              {billing && billing.status !== "active" && billing.status !== "not_connected" && billing.status !== "billing_skipped" && (
                <Button
                  onClick={startSubscribe}
                  disabled={billingBusy}
                  className="bg-[#16a34a] hover:bg-[#15803d]"
                  data-testid="subscription-subscribe-btn"
                >
                  {billingBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Subscribe via Shopify"}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* API Credentials */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Key className="w-5 h-5 text-[#16a34a]" />
              API Credentials
            </CardTitle>
            <CardDescription>
              Courier API configuration
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-slate-700">API URL</Label>
              <Input
                value={settings?.ff_api_url || ""}
                onChange={(e) => updateSettings("ff_api_url", e.target.value)}
                className="mt-1 border-slate-200 font-mono text-sm"
                placeholder="https://ffweb30201.pperfect.com/ecomService/v28/Json/"
                data-testid="api-url"
              />
            </div>
            <div>
              <Label className="text-slate-700">Account Number</Label>
              <Input
                value={settings?.ff_account || ""}
                onChange={(e) => updateSettings("ff_account", e.target.value)}
                className="mt-1 border-slate-200 font-mono"
                placeholder="e.g. your account number"
                data-testid="account-number"
              />
            </div>
            <div>
              <Label className="text-slate-700">Login Email</Label>
              <Input
                type="email"
                value={settings?.ff_email || ""}
                onChange={(e) => updateSettings("ff_email", e.target.value)}
                className="mt-1 border-slate-200"
                placeholder="your-email@company.co.za"
                data-testid="ff-email"
              />
              <p className="text-xs text-slate-500 mt-1">Email used to log into courier portal</p>
            </div>
            <div>
              <Label className="text-slate-700">Password</Label>
              <Input
                type="password"
                value={settings?.ff_password || ""}
                onChange={(e) => updateSettings("ff_password", e.target.value)}
                className="mt-1 border-slate-200"
                placeholder="Your courier password"
                data-testid="ff-password"
              />
              <p className="text-xs text-slate-500 mt-1">Usually your account number</p>
            </div>

            <Separator />

            <div className="flex items-center gap-4">
              <Button 
                variant="outline" 
                onClick={synchronise}
                disabled={testing}
                className="border-[#16a34a] text-[#16a34a] hover:bg-[#16a34a]/10"
                data-testid="sync-ff-btn"
              >
                {testing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Synchronising...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Synchronise
                  </>
                )}
              </Button>
              
              {connectionStatus && (
                <div className={`flex items-center gap-2 text-sm ${connectionStatus.success ? 'text-green-600' : 'text-red-600'}`}>
                  {connectionStatus.success ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <AlertCircle className="w-4 h-4" />
                  )}
                  {connectionStatus.message}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Default Sender */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="w-5 h-5 text-[#16a34a]" />
              Default Sender Details
            </CardTitle>
            <CardDescription>
              Your warehouse/pickup location information (used as "From" on waybills)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-slate-700">From (Company Name)</Label>
              <Input
                value={settings?.company_name || ""}
                onChange={(e) => updateSettings("company_name", e.target.value)}
                className="mt-1 border-slate-200"
                placeholder="Your Company Name"
                data-testid="company-name"
              />
            </div>
            <div>
              <Label className="text-slate-700">Place (Suburb Verification)</Label>
              <div className="mt-1">
                <PlaceSearchDropdown
                  value={{
                    town: settings?.warehouse_name || "",
                    place: settings?.place_id || null,
                    pcode: settings?.postal_code || ""
                  }}
                  onChange={(text) => {
                    updateSettings("warehouse_name", text);
                  }}
                  onPlaceSelect={(place) => {
                    // Update all three fields at once
                    setSettings(prev => ({
                      ...prev,
                      warehouse_name: place.town,
                      place_id: place.place,
                      postal_code: place.pcode || ""
                    }));
                  }}
                  placeholder="Search suburb..."
                  showSavedValue={true}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Search and select your suburb to verify the address</p>
            </div>
            <div>
              <Label className="text-slate-700">Postal Code</Label>
              <Input
                value={settings?.postal_code || ""}
                className="mt-1 border-slate-200 bg-slate-50"
                placeholder="Auto-filled from Place"
                readOnly
                data-testid="postal-code"
              />
            </div>
            <div>
              <Label className="text-slate-700">Address</Label>
              <div className="space-y-2 mt-1">
                <Input
                  value={settings?.address_line1 || ""}
                  onChange={(e) => updateSettings("address_line1", e.target.value)}
                  className="border-slate-200"
                  placeholder="Address line 1"
                  data-testid="address-line1"
                />
                <Input
                  value={settings?.address_line2 || ""}
                  onChange={(e) => updateSettings("address_line2", e.target.value)}
                  className="border-slate-200"
                  placeholder="Address line 2"
                />
                <Input
                  value={settings?.city || ""}
                  onChange={(e) => updateSettings("city", e.target.value)}
                  className="border-slate-200"
                  placeholder="City"
                  data-testid="city"
                />
                <Input
                  value={settings?.province || ""}
                  onChange={(e) => updateSettings("province", e.target.value)}
                  className="border-slate-200"
                  placeholder="Province"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-700">Contact</Label>
              <Input
                value={settings?.contact_person || ""}
                onChange={(e) => updateSettings("contact_person", e.target.value)}
                className="mt-1 border-slate-200"
                placeholder="Contact person name"
                data-testid="contact-person"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Telephone</Label>
                <Input
                  value={settings?.phone || ""}
                  onChange={(e) => updateSettings("phone", e.target.value)}
                  className="mt-1 border-slate-200"
                  placeholder="+27 21 000 0000"
                  data-testid="phone"
                />
              </div>
              <div>
                <Label className="text-slate-700">Cell</Label>
                <Input
                  value={settings?.cell || ""}
                  onChange={(e) => updateSettings("cell", e.target.value)}
                  className="mt-1 border-slate-200"
                  placeholder="+27 82 000 0000"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-700">Email</Label>
              <Input
                type="email"
                value={settings?.email || ""}
                onChange={(e) => updateSettings("email", e.target.value)}
                className="mt-1 border-slate-200"
                placeholder="shipping@company.co.za"
                data-testid="sender-email"
              />
            </div>
          </CardContent>
        </Card>

        {/* Printer Settings */}
        <Card className="border-slate-200 shadow-sm lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Printer className="w-5 h-5 text-[#16a34a]" />
              Print & Service Settings
            </CardTitle>
            <CardDescription>
              Configure default printing and service options
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-slate-700">Default Service Type</Label>
                <Select 
                  value={settings?.default_service || "ECO"} 
                  onValueChange={(v) => updateSettings("default_service", v)}
                >
                  <SelectTrigger className="mt-1 border-slate-200" data-testid="default-service">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ECO">Economy (ECO)</SelectItem>
                    <SelectItem value="LOC">Local (LOC)</SelectItem>
                    <SelectItem value="ONX">Overnight Express (ONX)</SelectItem>
                    <SelectItem value="SDX">Same Day Express (SDX)</SelectItem>
                    <SelectItem value="RDX">Road Express (RDX)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between pt-6">
                <div>
                  <Label className="text-slate-700">Auto-Print on Creation</Label>
                  <p className="text-xs text-slate-500 mt-1">Automatically print waybill after generating</p>
                </div>
                <Switch
                  checked={settings?.auto_print || false}
                  onCheckedChange={(v) => updateSettings("auto_print", v)}
                  data-testid="auto-print"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SettingsPage;
